// Drill-down modal for the Store and Purchase dashboard's tiles. Rows come from
// GetSNPPendingDetails. For Pending Indents / MRR / PO: every document still under process, with
// the workflow level it is pending at (Status + 1) and the role(s) configured at that level — the
// level chips on top summarise the count per level/role and double as a filter for the table.
// For the PO-expiry tiles: approved, open POs expiring within 5 days, with expiry date/days left.
import React, { useMemo, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../utilities/formatCurrency';

export const COLUMNS = {
    docNo: (label) => ({ label, render: (r) => <span className="font-medium text-gray-900 dark:text-white">{r.DocNo}</span>, nowrap: true }),
    refNo: (label) => ({ label, render: (r) => r.RefNo || '-', nowrap: true }),
    docType: { label: 'Type', render: (r) => r.DocType, nowrap: true },
    party: (label = 'Vendor') => ({ label, render: (r) => r.PartyName || '-' }),
    remarks: { label: 'Remarks', render: (r) => r.Remarks || '-' },
    cc: {
        label: 'Cost Center',
        render: (r) => (
            <>
                {r.CCCode}
                {r.CCName ? <span className="text-gray-400 dark:text-gray-500"> · {r.CCName}</span> : null}
            </>
        ),
    },
    date: (label) => ({ label, render: (r) => r.DocDate, nowrap: true }),
    amount: { label: 'Amount (₹)', render: (r) => (r.Amount == null ? '-' : formatCurrency(r.Amount)), align: 'right', nowrap: true },
    expiry: { label: 'Expiry Date', render: (r) => r.ExpiryDate, nowrap: true },
    daysLeft: {
        label: 'Days Left',
        align: 'center',
        render: (r) => (
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${r.DaysLeft <= 1
                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'}`}>
                {r.DaysLeft === 0 ? 'Today' : `${r.DaysLeft} day${r.DaysLeft === 1 ? '' : 's'}`}
            </span>
        ),
    },
};

// Per-tile modal title + table columns. Workflow tiles (showLevels) get Level / Pending With columns
// and the level filter chips appended; the expiry tiles are already-approved POs, so they don't.
export const PENDING_DOC_CONFIG = {
    Indent: {
        title: 'Indents Under Process',
        noun: 'indent',
        columns: [COLUMNS.docNo('Indent No'), COLUMNS.cc, COLUMNS.date('Raised On'), COLUMNS.amount],
    },
    MRR: {
        title: 'MRRs Under Process',
        noun: 'MRR',
        columns: [COLUMNS.docNo('MRR No'), COLUMNS.refNo('PO No'), COLUMNS.party(), COLUMNS.cc, COLUMNS.date('Received On')],
    },
    PO: {
        title: 'POs Under Process',
        noun: 'PO',
        columns: [COLUMNS.docNo('PO No'), COLUMNS.docType, COLUMNS.party(), COLUMNS.cc, COLUMNS.date('PO Date'), COLUMNS.amount],
    },
    SupplierPOExpiry: {
        title: 'Supplier POs Nearing Expiry',
        noun: 'supplier PO',
        summary: 'expiring in the next 5 days',
        showLevels: false,
        columns: [COLUMNS.docNo('PO No'), COLUMNS.refNo('Indent No'), COLUMNS.party(), COLUMNS.cc, COLUMNS.date('PO Date'), COLUMNS.amount, COLUMNS.expiry, COLUMNS.daysLeft],
    },
    SPPOExpiry: {
        title: 'SPPOs Nearing Expiry',
        noun: 'SPPO',
        summary: 'expiring in the next 5 days',
        showLevels: false,
        columns: [COLUMNS.docNo('SPPO No'), COLUMNS.party(), COLUMNS.cc, COLUMNS.date('Start Date'), COLUMNS.amount, COLUMNS.expiry, COLUMNS.daysLeft],
    },
};

const levelKey = (row) => `${row.PendingLevel ?? '-'}|${row.PendingWithRole || ''}`;

// configs defaults to the Store and Purchase tiles; other dashboards pass their own map built from COLUMNS.
const PendingDocumentsModal = ({ open, onClose, type, loading, rows = [], configs = PENDING_DOC_CONFIG }) => {
    const [activeLevel, setActiveLevel] = useState(null);
    const config = configs[type];

    const levels = useMemo(() => {
        const map = new Map();
        rows.forEach((row) => {
            const key = levelKey(row);
            const entry = map.get(key) || { key, level: row.PendingLevel, role: row.PendingWithRole, count: 0 };
            entry.count += 1;
            map.set(key, entry);
        });
        return [...map.values()].sort((a, b) => (a.level ?? 99) - (b.level ?? 99));
    }, [rows]);

    if (!open || !config) return null;

    const visibleRows = activeLevel ? rows.filter((row) => levelKey(row) === activeLevel) : rows;
    const showLevels = config.showLevels !== false;
    const columns = !showLevels ? config.columns : [
        ...config.columns,
        {
            label: 'Level',
            align: 'center',
            render: (r) => (
                <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                    {r.PendingLevel ?? '-'}
                </span>
            ),
        },
        {
            label: 'Pending With',
            render: (r) => (
                <>
                    {r.PendingWithRole}
                    {r.PendingLevel === 1 && (
                        <span className="ml-2 text-[11px] text-red-500 dark:text-red-400">(Returned to raiser)</span>
                    )}
                </>
            ),
        },
    ];

    const handleClose = () => {
        setActiveLevel(null);
        onClose();
    };

    const chipClass = (active) => `px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active
        ? 'bg-orange-500 border-orange-500 text-white'
        : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-orange-300'}`;
    const alignClass = (align) => (align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-5xl max-h-[85vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-orange-50 dark:bg-orange-900/20 rounded-t-2xl flex-shrink-0">
                    <div>
                        <h2 className="text-base font-bold text-gray-800 dark:text-gray-100">{config.title}</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {loading ? 'Loading…' : `${rows.length} ${config.noun}${rows.length === 1 ? '' : 's'} ${config.summary || 'pending across workflow levels'}`}
                        </p>
                    </div>
                    <button onClick={handleClose}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {showLevels && !loading && levels.length > 0 && (
                    <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-2 flex-shrink-0">
                        <button onClick={() => setActiveLevel(null)} className={chipClass(!activeLevel)}>
                            All · {rows.length}
                        </button>
                        {levels.map((lvl) => (
                            <button
                                key={lvl.key}
                                onClick={() => setActiveLevel(lvl.key === activeLevel ? null : lvl.key)}
                                className={chipClass(lvl.key === activeLevel)}
                            >
                                Level {lvl.level ?? '-'} · {lvl.role || 'Not configured'} · {lvl.count}
                            </button>
                        ))}
                    </div>
                )}

                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 gap-2 text-gray-400 dark:text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
                        </div>
                    ) : visibleRows.length === 0 ? (
                        <div className="flex items-center justify-center py-12 text-gray-400 dark:text-gray-500 text-sm">
                            No {config.noun}s {showLevels ? 'under process' : config.summary}
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                                <tr>
                                    {columns.map((col) => (
                                        <th key={col.label}
                                            className={`px-4 py-2 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap ${alignClass(col.align)}`}>
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {visibleRows.map((row, idx) => (
                                    <tr key={`${row.DocType}-${row.DocNo}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                        {columns.map((col) => (
                                            <td key={col.label}
                                                className={`px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${alignClass(col.align)} ${col.nowrap ? 'whitespace-nowrap' : ''}`}>
                                                {col.render(row)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PendingDocumentsModal;
